<script setup>
import Comment from './Comment.vue'
import { useTextareaAutosize } from '@vueuse/core'
import { nextTick, onMounted, reactive, ref } from 'vue'
import axios from 'axios'
import { router, usePage } from '@inertiajs/vue3'
import SendSvg from "@svg/new-send.svg";
import NotificationSvg from '@svg/new-notification.svg'
import Toggle from '../Toggle.vue'
import CommentsPagination from '../CommentsPagination.vue'
import VueScrollTo from "vue-scrollto";

const page = usePage()

const props = defineProps({
    index: String,
    store: String,
})

const commentsMeta = ref(null)
const comments = ref([])

const { textarea, input } = useTextareaAutosize()

const loadComments = (url) => {
    return axios.get(url).then((response) => {
        comments.value = response.data.data
        commentsMeta.value = response.data.meta
    })
}

const handlePagination = (url) => {
    loadComments(url).then(() => {
        VueScrollTo.scrollTo('#comments', { duration: 0 })
    })
}

onMounted(() => {
    if (page.props.comment) {
        axios.get(`/comments/${page.props.comment}/page`).then((response) => {
            loadComments(`${props.index}?page=${response.data.page}`).then(() => {
                VueScrollTo.scrollTo(`#comment-${page.props.comment}`, { duration: 0, offset: -50 })
                page.props.comment = null
            })
        })
    }

    loadComments(props.index)
    router.restore('comment.body')
})

const form = reactive({
    body: null,
    subscribe: true
})

const submit = () => {
    axios.post(props.store, form).then((response) => {
        comments.value.unshift(response.data)
        loadComments(props.index)
    }).then(() => {
        form.body = null
        input.value = null
    })
}

const handleDeletedComment = (id) => {
    comments.value = comments.value.filter(c => {
        c.children = c.children.filter(child => child.id !== id)
        return c.id !== id
    })
}
</script>

<template>
    <div id="comments">
        <form v-if="$page.props.auth.user" v-on:submit.prevent="submit" class="mt-8 bg-[rgba(27,32,74,1)] px-8 py-5 rounded-2xl">
            <div class="flex items-start flex-wrap">
                <div class="flex-grow w-full md:w-auto">
                    <textarea dusk="comments-create-textarea" ref="textarea" class="border-0 bg-transparent w-full resize-none placeholder:text-[rgba(148,162,209,1)] text-[rgba(148,162,209,1)] text-sm focus:ring-0 p-0 leading-normal mt-2" placeholder="Write your comment here" required v-model="input" v-model.body="form.body"></textarea>
                </div>

                <button dusk="comments-create-button" :disabled="form.processing" class="mt-1 md:mt-0 px-3 py-2.5 rounded-lg leading-none bg-[rgba(107,96,255,1)] text-[rgba(233,232,255,1)] font-medium inline-flex items-center text-sm space-x-1" aria-label="Post comment">
                    <SendSvg class="size-5 -ml-0.5" />
                    <span>Post</span>
                </button>
            </div>
            <div
                class="mt-4 border rounded-lg p-2 border-[rgba(35,40,86,1)] flex items-center"
            >
                <label for="subscribe" class="flex items-center flex-grow">
                    <NotificationSvg class="text-[rgba(112,120,176,1)] size-5 shrink-0" />
                    <span class="text-[rgba(112,120,176,1)] text-xs font-medium ml-2 transition-colors leading-tight">Subscribe to reply notifications</span>
                </label>
                <Toggle id="subscribe" name="subscribe" v-model="form.subscribe" />
            </div>
        </form>

        <div class="mt-8 space-y-6" v-if="comments.length">
            <Comment
                v-for="comment in comments"
                :key="comment.id"
                :comment="comment"
                :show-reply-button="comment.children.length === 0"
                v-on:comment-deleted="handleDeletedComment"
            />

            <div class="flex justify-center" v-if="commentsMeta.last_page > 1">
                <CommentsPagination :meta="commentsMeta" v-on:navigated="handlePagination($event)" />
            </div>
        </div>
        <div v-else class="mt-8 bg-[rgba(27,32,74,1)] px-8 py-10 text-[rgba(112,120,176,1)] rounded-2xl flex items-start justify-center text-sm font-medium">
            No comments, yet. Be the first!
        </div>
    </div>
</template>
